<template>
    <div class="flex w-full flex-col  at600:flex-row gap-4 items-center justify-between mb-8">
        <h1 class="sr-only">Resources</h1>
        <h3 class="font-semibold whitespace-nowrap text-xl at1024:text-3xl">
            <template v-if="!userSelectionsMade">{{ $t('New Videos') }}</template>
            <template v-else>{{ searchTotalResults }} results for "{{ searchTerm}}"</template>
        </h3>
        <div class="w-full max-w-[600px] flex justify-end flex-col-reverse at600:flex-row items-center gap-4">
            <div class="w-full at600:w-auto flex justify-end gap-4  items-center">
                <SeclockMultiselect
                    v-if="settings.is_sourcing_mode"
                    class="w-full min-w-64"
                    :option-is-object="true"
                    label-by="display_name"
                    track-by="display_name"
                    value-by="display_name"
                    v-model="selectedManufacturer"
                    :options="manufacturers"
                    :searchable="true"
                    :placeholder="$t('Manufacturers')"
                    :can-clear="false"
                    mode="single"
                    :large="false"
                    @change="navigateToShow($event)"
                />
                <SearchInput v-model="searchTerm" :medium="true" />

            </div>
        </div>
    </div>
    <div v-if="userSelectionsMade && searchHasResults" ref="results">
        <ul class="grid gap-8 grid-cols-1 at1024:grid-cols-2">
            <li v-for="resource in searchResults">
                <ResourceCard :resource="resource" :elasticsearch="true" />
            </li>
        </ul>
        <div class="mt-8">
            <SearchPagination
                v-if="searchHasResults && searchTotalResults > searchResultsPerPage"
                :total-records="Math.min(searchTotalResults, 10000)"
                :per-page="searchResultsPerPage"
                :current-page="searchCurrentPage"
                @update:currentPage="
                    (page) => {
                        searchCurrentPage = page;
                        scrollToTop();
                    }
                "
            />
        </div>
    </div>
    <div v-if="userSelectionsMade && !searchHasResults && !searchIsLoading">
        <RecordsNotFound message="There are no resources that match your query." />
    </div>
    <div v-if="!userSelectionsMade" class="grid gap-8 grid-cols-1 at1024:grid-cols-2">
        <ResourceCard :resource="resource" v-for="resource in newVideos" />
    </div>
    <div v-if="searchIsLoading">
        <LoadingSpinner :large="true" />
    </div>
</template>

<script setup>
import {useAppSearchGeneric, useSettingsLoader, useUser} from "@/Composables";
import {computed, inject, ref, watch} from "vue";
import {router} from "@inertiajs/vue3";
import {
    ResourceCard,
    SearchInput,
    SeclockMultiselect,
    RecordsNotFound,
    LoadingSpinner,
    SearchPagination,
} from "@/Components";

const route = inject('route');

const props = defineProps({
    manufacturers: {
        type: Array,
        required: true,
    },
    newVideos: {
        type: Array,
        required: true,
    },
});

const {
    searchTerm,
    searchIsLoading,
    searchResults,
    searchTotalResults,
    searchHasResults,
    searchFacets,
    searchWasSearched,
    searchClearFilters,
    searchState,
    searchReset,
    searchSuggestions,
    searchTrackClick,
    searchAppliedFilters,
    searchCurrentPage,
    searchSortBy,
    searchResultsPerPage,
    searchAddFilter,
    searchSetFilter,
    searchRemoveFilter,
} = useAppSearchGeneric(useSettingsLoader().settings('resourceSearch'));

const results = ref(null);

const { settings } = useUser();

const userSelectionsMade = computed(() => {
    return !!(searchState.value.filters?.length > 1 || searchTerm.value?.length);
});

const scrollToTop = () => results.value.scrollTop;

const selectedManufacturer = ref('');

const navigateToShow = (event) => router.visit(route('web.catalog.resources.show', event.slug));
</script>
